import {Graphics} from "pixi.js";

// Renders an SVG path's `d` data into a pixi Graphics. pixi v7 has no built-in
// SVG path parser, so this implements the path grammar used by typical icon
// sets: M/L/H/V/C/S/Q/T/A/Z, absolute and relative. Set beginFill()/lineStyle()
// on the Graphics yourself before calling — this only emits the geometry.
//
// Coordinates are scaled/offset on emit, so a 24x24 icon path drawn at
// scaleX = scaleY = size/24 fits a `size`px box. Affine scale+translate maps
// beziers to beziers, so reflections (S/T) and arc-to-bezier conversion are done
// in the path's own units and transformed only when handed to the Graphics.
export function drawSvgPath(g: Graphics, d: string, opts?: {
    scaleX?: number;
    scaleY?: number;
    offsetX?: number;
    offsetY?: number;
}) {
    const scaleX = opts?.scaleX ?? 1;
    const scaleY = opts?.scaleY ?? 1;
    const offX = opts?.offsetX ?? 0;
    const offY = opts?.offsetY ?? 0;

    const X = (x: number) => x * scaleX + offX;
    const Y = (y: number) => y * scaleY + offY;

    // current point, subpath start, and the previous cubic/quad control points
    // (used to reflect for S/T) — all in path units.
    let curX = 0, curY = 0;
    let startX = 0, startY = 0;
    let ctrlC: [number, number] | null = null;
    let ctrlQ: [number, number] | null = null;

    let i = 0;
    const n = d.length;
    let cmd = "";

    const isSep = (c: string) => c === " " || c === "," || c === "\t" || c === "\n" || c === "\r";
    const isDigit = (c: string) => c >= "0" && c <= "9";

    function skipSep() {
        while (i < n && isSep(d[i])) i++;
    }

    function readNumber(): number {
        skipSep();
        const start = i;
        if (d[i] === "+" || d[i] === "-") i++;
        while (i < n && isDigit(d[i])) i++;
        if (d[i] === ".") { i++; while (i < n && isDigit(d[i])) i++; }
        if (d[i] === "e" || d[i] === "E") {
            i++;
            if (d[i] === "+" || d[i] === "-") i++;
            while (i < n && isDigit(d[i])) i++;
        }
        return parseFloat(d.slice(start, i));
    }

    // arc large-arc/sweep flags are single 0/1 digits that may be packed tight
    // against the next number (e.g. "a5 5 0 0015 0"), so read exactly one char.
    function readFlag(): number {
        skipSep();
        const c = d[i];
        i++;
        return c === "1" ? 1 : 0;
    }

    function emitArc(rx: number, ry: number, phiDeg: number, large: number, sweep: number, ex: number, ey: number) {
        for (const seg of arcToBeziers(curX, curY, rx, ry, phiDeg * Math.PI / 180, large, sweep, ex, ey)) {
            g.bezierCurveTo(X(seg[0]), Y(seg[1]), X(seg[2]), Y(seg[3]), X(seg[4]), Y(seg[5]));
        }
        curX = ex; curY = ey;
    }

    while (true) {
        skipSep();
        if (i >= n) break;

        const c = d[i];
        if ((c >= "a" && c <= "z") || (c >= "A" && c <= "Z")) {
            cmd = c;
            i++;
        }
        // else: implicit repeat of the previous command (M/m falls through to
        // L/l for trailing pairs, handled below).

        const rel = cmd >= "a";
        const bx = rel ? curX : 0;
        const by = rel ? curY : 0;

        switch (cmd) {
            case "M": case "m": {
                curX = bx + readNumber();
                curY = by + readNumber();
                startX = curX; startY = curY;
                g.moveTo(X(curX), Y(curY));
                ctrlC = ctrlQ = null;
                // subsequent coordinate pairs are treated as lineto.
                cmd = rel ? "l" : "L";
                break;
            }
            case "L": case "l": {
                curX = bx + readNumber();
                curY = by + readNumber();
                g.lineTo(X(curX), Y(curY));
                ctrlC = ctrlQ = null;
                break;
            }
            case "H": case "h": {
                curX = bx + readNumber();
                g.lineTo(X(curX), Y(curY));
                ctrlC = ctrlQ = null;
                break;
            }
            case "V": case "v": {
                curY = by + readNumber();
                g.lineTo(X(curX), Y(curY));
                ctrlC = ctrlQ = null;
                break;
            }
            case "C": case "c": {
                const c1x = bx + readNumber(), c1y = by + readNumber();
                const c2x = bx + readNumber(), c2y = by + readNumber();
                curX = bx + readNumber(); curY = by + readNumber();
                g.bezierCurveTo(X(c1x), Y(c1y), X(c2x), Y(c2y), X(curX), Y(curY));
                ctrlC = [c2x, c2y]; ctrlQ = null;
                break;
            }
            case "S": case "s": {
                // first control is the reflection of the previous cubic control
                // about the current point (or the current point if none).
                const c1x = ctrlC ? 2 * curX - ctrlC[0] : curX;
                const c1y = ctrlC ? 2 * curY - ctrlC[1] : curY;
                const c2x = bx + readNumber(), c2y = by + readNumber();
                curX = bx + readNumber(); curY = by + readNumber();
                g.bezierCurveTo(X(c1x), Y(c1y), X(c2x), Y(c2y), X(curX), Y(curY));
                ctrlC = [c2x, c2y]; ctrlQ = null;
                break;
            }
            case "Q": case "q": {
                const cqx = bx + readNumber(), cqy = by + readNumber();
                curX = bx + readNumber(); curY = by + readNumber();
                g.quadraticCurveTo(X(cqx), Y(cqy), X(curX), Y(curY));
                ctrlQ = [cqx, cqy]; ctrlC = null;
                break;
            }
            case "T": case "t": {
                const cqx = ctrlQ ? 2 * curX - ctrlQ[0] : curX;
                const cqy = ctrlQ ? 2 * curY - ctrlQ[1] : curY;
                curX = bx + readNumber(); curY = by + readNumber();
                g.quadraticCurveTo(X(cqx), Y(cqy), X(curX), Y(curY));
                ctrlQ = [cqx, cqy]; ctrlC = null;
                break;
            }
            case "A": case "a": {
                const rx = readNumber(), ry = readNumber();
                const phi = readNumber();
                const large = readFlag(), sweep = readFlag();
                const ex = bx + readNumber(), ey = by + readNumber();
                emitArc(rx, ry, phi, large, sweep, ex, ey);
                ctrlC = ctrlQ = null;
                break;
            }
            case "Z": case "z": {
                g.closePath();
                curX = startX; curY = startY;
                ctrlC = ctrlQ = null;
                break;
            }
            default:
                // unknown command — bail rather than spin forever.
                return;
        }
    }
}

// converts an SVG elliptical-arc segment to a series of cubic beziers in the
// same coordinate space. Standard endpoint -> center parameterisation (SVG impl
// notes appendix), one bezier per <=90deg of sweep.
function arcToBeziers(
    x1: number, y1: number, rx: number, ry: number, phi: number,
    large: number, sweep: number, x2: number, y2: number,
): number[][] {
    if (rx === 0 || ry === 0) return [[x1, y1, x2, y2, x2, y2]];

    rx = Math.abs(rx);
    ry = Math.abs(ry);

    const sinPhi = Math.sin(phi);
    const cosPhi = Math.cos(phi);

    const dx = (x1 - x2) / 2;
    const dy = (y1 - y2) / 2;
    const x1p = cosPhi * dx + sinPhi * dy;
    const y1p = -sinPhi * dx + cosPhi * dy;

    let rxs = rx * rx, rys = ry * ry;
    const x1ps = x1p * x1p, y1ps = y1p * y1p;

    // scale the radii up if they're too small to span the endpoints.
    const lambda = x1ps / rxs + y1ps / rys;
    if (lambda > 1) {
        const s = Math.sqrt(lambda);
        rx *= s; ry *= s;
        rxs = rx * rx; rys = ry * ry;
    }

    const sign = large === sweep ? -1 : 1;
    let num = rxs * rys - rxs * y1ps - rys * x1ps;
    num = num < 0 ? 0 : num;
    const coef = sign * Math.sqrt(num / (rxs * y1ps + rys * x1ps));
    const cxp = coef * (rx * y1p / ry);
    const cyp = coef * (-ry * x1p / rx);

    const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
    const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;

    const ang = (ux: number, uy: number, vx: number, vy: number) => {
        const dot = ux * vx + uy * vy;
        const len = Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy));
        let a = Math.acos(Math.max(-1, Math.min(1, dot / len)));
        if (ux * vy - uy * vx < 0) a = -a;
        return a;
    };

    const theta1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
    let dtheta = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
    if (!sweep && dtheta > 0) dtheta -= 2 * Math.PI;
    if (sweep && dtheta < 0) dtheta += 2 * Math.PI;

    const segments = Math.max(1, Math.ceil(Math.abs(dtheta / (Math.PI / 2))));
    const delta = dtheta / segments;
    const t = (4 / 3) * Math.tan(delta / 4);

    const out: number[][] = [];
    let sx = x1, sy = y1;
    let th = theta1;

    for (let s = 0; s < segments; s++) {
        const cosTh = Math.cos(th), sinTh = Math.sin(th);
        const th2 = th + delta;
        const cosTh2 = Math.cos(th2), sinTh2 = Math.sin(th2);

        const ex = cosPhi * rx * cosTh2 - sinPhi * ry * sinTh2 + cx;
        const ey = sinPhi * rx * cosTh2 + cosPhi * ry * sinTh2 + cy;

        const dx1 = -rx * cosPhi * sinTh - ry * sinPhi * cosTh;
        const dy1 = -rx * sinPhi * sinTh + ry * cosPhi * cosTh;
        const dx2 = -rx * cosPhi * sinTh2 - ry * sinPhi * cosTh2;
        const dy2 = -rx * sinPhi * sinTh2 + ry * cosPhi * cosTh2;

        out.push([
            sx + t * dx1, sy + t * dy1,
            ex - t * dx2, ey - t * dy2,
            ex, ey,
        ]);

        sx = ex; sy = ey; th = th2;
    }

    return out;
}
